import { SystemTag, TagDef, TagValue, TagValueType } from "../types";
import * as chrono from "chrono-node";
import { isDateValue, localDate, parseLocalDate } from "../dates";
import type { DateValue } from "../dates";

// ── The canonical string ↔ TagValue mapping ─────────────────────────────────
// These two are THE place strings become typed tag values and vice versa. Used
// everywhere a value is entered or shown: card metadata editing, the tag-manager
// value list, suggestions. The backend only ever sees the structured TagValue —
// it never parses these strings.

// A typed value as a display/edit string. Inverse of `parseValue` for the
// canonical forms it produces.
export function formatValue(value: TagValue): string {
  switch (value.type) {
    case "none":
      return "";
    case "number":
      return String(value.value);
    default:
      return value.value; // string / date / datetime / duration
  }
}

// Parse user input into a typed value, or null if it doesn't conform. Supports
// natural language for dates/datetimes (via chrono) and durations ("2 days 3
// hours" → ISO-8601), and normalizes to the canonical form `formatValue`
// expects. `string` accepts anything (raw, untrimmed); `none` is value-less.
export function parseValue(type: TagValueType, raw: string): TagValue | null {
  switch (type) {
    case "none":
      return { type: "none" };

    case "string":
      return { type: "string", value: raw };

    case "number": {
      const v = raw.trim();
      const n = parseFloat(v);
      return Number.isFinite(n) && /^-?\d*\.?\d+$/.test(v) ? { type: "number", value: n } : null;
    }

    case "date": {
      const d = chrono.parseDate(raw);
      return d ? { type: "date", value: localDate(d) } : null;
    }

    case "datetime": {
      const d = chrono.parseDate(raw);
      return d ? { type: "datetime", value: d.toISOString() } : null;
    }

    case "duration": {
      const norm = normalizeIso8601Duration(raw.trim());
      return ISO_DURATION_RE.test(norm) && /\d/.test(norm)
        ? { type: "duration", value: norm }
        : null;
    }
  }
}

// ── Date pickers ─────────────────────────────────────────────────────────────
// A card edits a date tag with <input type="date"> and a datetime tag with
// <input type="datetime-local">. The second input shows local time with no
// time zone.

function pickerValue(value: DateValue): string {
  if (value.type === "date") return value.value;
  const d = new Date(value.value);
  // toTimeString() starts with the local "HH:MM".
  return Number.isNaN(d.getTime()) ? "" : `${localDate(d)}T${d.toTimeString().slice(0, 5)}`;
}

// This function returns null when the picker is empty or its value is not
// complete.
function parsePickerValue(type: "date" | "datetime", raw: string): TagValue | null {
  if (type === "date") return parseLocalDate(raw) ? { type: "date", value: raw } : null;
  // A date-time string with no time zone is local time.
  const d = new Date(raw);
  return raw && !Number.isNaN(d.getTime()) ? { type: "datetime", value: d.toISOString() } : null;
}

const ISO_DURATION_RE = /^P(\d+Y)?(\d+M)?(\d+W)?(\d+D)?(T(\d+H)?(\d+M)?(\d+S)?)?$/;

function normalizeIso8601Duration(raw: string): string {
  // Already ISO 8601 (P1DT2H, PT30M, P2W, …)?
  if (ISO_DURATION_RE.test(raw)) return raw;
  // Natural language like "2 days 3 hours" → P2DT3H.
  const parts: string[] = [];
  const timeParts: string[] = [];
  const patterns: [RegExp, (n: number) => void][] = [
    [/(\d+)\s*y(ear)?s?/i, (n) => parts.push(`${n}Y`)],
    [/(\d+)\s*mo(nth)?s?/i, (n) => parts.push(`${n}M`)],
    [/(\d+)\s*w(eek)?s?/i, (n) => parts.push(`${n}W`)],
    [/(\d+)\s*d(ay)?s?/i, (n) => parts.push(`${n}D`)],
    [/(\d+)\s*h(our)?s?/i, (n) => timeParts.push(`${n}H`)],
    [/(\d+)\s*m(in(ute)?)?s?/i, (n) => timeParts.push(`${n}M`)],
    [/(\d+)\s*s(ec(ond)?)?s?/i, (n) => timeParts.push(`${n}S`)],
  ];
  for (const [re, push] of patterns) {
    const m = re.exec(raw);
    if (m) push(Number(m[1]));
  }
  if (!parts.length && !timeParts.length) return raw;
  const timeStr = timeParts.length ? `T${timeParts.join("")}` : "";
  return `P${parts.join("")}${timeStr}`;
}

// ── Tag editing ──────────────────────────────────────────────────────────────
// The view and update() both use these rules, so the two always agree.

// An open date or datetime tag uses a native picker of that type. All other
// tags (null) use text, with a list when the tag has predefined values.
export function pickerType(def: TagDef): "date" | "datetime" | null {
  const { valueType } = def;
  return def.allowAll && (valueType === "date" || valueType === "datetime") ? valueType : null;
}

// The editor of a tag starts with this draft.
export function draftOf(def: TagDef, value: TagValue | undefined): string {
  if (!value) return "";
  return pickerType(def) && isDateValue(value) ? pickerValue(value) : formatValue(value);
}

// This function returns null when the draft is not a valid value of the tag.
export function parseDraft(def: TagDef, draft: string): TagValue | null {
  const type = pickerType(def);
  const value = type ? parsePickerValue(type, draft) : parseValue(def.valueType, draft);
  return value && valueAllowed(def, value) ? value : null;
}

// Free tags accept any value. A closed list accepts only its predefined values,
// compared in canonical string form.
function valueAllowed(def: TagDef, value: TagValue): boolean {
  if (def.allowAll || def.valueType === "none") return true;
  const canonical = formatValue(value);
  return def.values.some((v) => formatValue(v) === canonical);
}

// ── Derived helpers ──────────────────────────────────────────────────────────

// View an assignable system tag (e.g. template) as a registry-style def so it
// can flow through the same card-tag UI (picker, default value, badge).
export function systemTagToDef(t: SystemTag): TagDef {
  return { name: t.name, valueType: t.valueType, values: [], allowAll: true };
}

// The value a tag takes when first added to a card. A closed enum (allowAll off)
// starts at its first predefined value; otherwise each type has a neutral
// default.
export function defaultTagValue(def: TagDef): TagValue {
  if (def.valueType === "none") return { type: "none" };
  if (!def.allowAll && def.values.length > 0) return def.values[0]!;
  switch (def.valueType) {
    case "string":
      return { type: "string", value: "" };
    case "number":
      return { type: "number", value: 0 };
    case "date":
      return parseValue("date", "today")!;
    case "datetime":
      return parseValue("datetime", "now")!;
    case "duration":
      return { type: "duration", value: "" };
  }
}
